import { useState, useEffect, useCallback } from "react";
import DataTable from "components/utils/DataTable";
import styles from "./styles.module.css";
import { Paper, Table, TableBody, TableCell, TableContainer, TableRow } from "@mui/material";
import ViewMoreIcon from "images/svg/note.svg";
import SyncIcon from "images/svg/refresh-2.svg";
import LocalSync from "images/svg/computer-sync.svg";
import TickIcon from "images/svg/syncIcon.svg";
import EmptyState from "components/utils/EmptyState/EmptyState";
import ShiftPaymentCashBookPopup from "components/popups/CashBookPopup/ShiftPaymentCashBookPopup";
import SkeletonLoading from "components/utils/Loading/SkeletonLoading";
import { db } from "helpers/DBHelper";
import * as ShiftHelper from "helpers/ShiftHelper";
import { SALE_SYNC_STATUS, SHIFT_STATUS, STORAGE_KEYS, EVENTS } from "const";
import { eventsEmitter } from "utils/events";
import { roundOff } from "helpers/NumberFormatHelper";
import * as StringHelper from "helpers/StringHelper";
import SaleDetailsDialog from "components/popups/SaleDetailsDialog/SaleDetailsDialog";
import DeletedCartsPopup from "components/popups/DeletedCartsPopup/DeletedCartsPopup";
import * as DeletedCartHelper from "helpers/DeletedCartHelper";
import EditedSalesPopup from "components/popups/EditedSalesPopup/EditedSalesPopup";
import * as EditedSaleHelper from "helpers/EditedSaleHelper";
import DateTimeHelpers from "helpers/DateTimeHelpers";
import * as SalesHelper from "helpers/SalesHelper";
import PrinterImg from "images/svg/printer.svg";
import * as ReportHelper from "helpers/ReportHelper";
import ButtonRed from "components/utils/CustomFields/ButtonRed";

const ShiftReport = ({ shiftId }) => {
  const [loading, setLoading] = useState(true);
  const [shiftOverview, setShiftOverview] = useState({});
  const [paymentModeSummaries, setPaymentModeSummaries] = useState([]);
  const [shiftOrders, setShiftOrders] = useState([]);
  const [selectedCashBook, setSelectedCashBook] = useState(null);
  const [selectedSaleId, setSelectedSaleId] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [showDeletedCarts, setShowDeletedCarts] = useState(false);
  const [deletedCarts, setDeletedCarts] = useState([]);
  const [showEditedSales, setShowEditedSales] = useState(false);
  const [editedSales, setEditedSales] = useState([]);

  const addBalance = (transactions) => {
    let balance = 0;
    return transactions.map((tnx) => {
      balance += (tnx.credit || 0) - (tnx.debit || 0);
      return { ...tnx, balance };
    });
  };

  const showViewCashBook = async (paymentMode) => {
    try {
      const modeId = paymentMode.id || paymentMode.payment_mode?.id;

      const localResult = await ShiftHelper.getLocalShiftTransactions({
        shiftId,
        modeId,
        paymentMode,
        shiftOverview,
        addBalance,
        db,
      });

      if (localResult) {
        setSelectedCashBook(localResult);
        return;
      }
    } catch (err) {
      console.error("Error opening cashbook:", err);
    }
  };

  const loadShiftData = useCallback(
    async (showLoader = true) => {
      try {
        if (showLoader) setLoading(true);
        const data = await ShiftHelper.loadShiftHelper(shiftId);
        setShiftOverview(data.shiftOverview);
        setPaymentModeSummaries(data.paymentModeSummaries);
        setShiftOrders(data.shiftOrders);

        const currentShiftId = shiftId || localStorage.getItem(STORAGE_KEYS.SHIFT_ID);
        const deleted = await DeletedCartHelper.getDeletedCartsByShift(currentShiftId);
        setDeletedCarts(deleted || []);

        const edited = await EditedSaleHelper.getEditedSalesByShift(currentShiftId);
        setEditedSales(edited || []);
      } catch (err) {
        console.error("Failed to load shift data:", err);
      } finally {
        if (showLoader) setLoading(false);
      }
    },
    [shiftId],
  );

  const paymentColumns = [
    {
      id: "payment",
      label: "Payment",
      type: "component",
      viewRender: (row) => <span className={styles.paymentBadge}>{row.payment}</span>,
    },
    { id: "opening", label: "Opening", type: "callback", viewRender: (row) => roundOff(row.opening) },
    {
      id: "in",
      label: "In",
      type: "callback",
      viewRender: (row) => {
        const totalIn = row.transactions.reduce((sum, t) => sum + (t.credit || 0), 0);
        return roundOff(totalIn);
      },
    },
    {
      id: "out",
      label: "Out",
      type: "callback",
      viewRender: (row) => {
        const totalOut = row.transactions.reduce((sum, t) => sum + (t.debit || 0), 0);
        return roundOff(totalOut);
      },
    },
    { id: "closing", label: "Closing", type: "callback", viewRender: (row) => roundOff(row.closing) },
    {
      id: "action",
      label: "Action",
      type: "component",
      minWidth: "120px",
      maxWidth: "140px",
      viewRender: (row) => (
        <div
          style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}
          onClick={() => showViewCashBook(row)}
        >
          <img src={ViewMoreIcon} alt="View more" />
          <span style={{ color: "#6155F5", fontSize: 12, fontWeight: 600 }}>View more</span>
        </div>
      ),
    },
  ];

  const shiftOrdersColumns = [
    { id: "billNo", label: "Bill No", type: "text" },
    { id: "paymentMode", label: "Payment Mode", type: "text" },
    { id: "dateTime", label: "Date & Time", type: "text", width: "160px" },
    { id: "name", label: "Name", type: "text" },
    { id: "type", label: "Type", type: "text" },
    {
      id: "syncStatus",
      label: "Sync Status",
      type: "component",
      viewRender: (row) => {
        const status = row.sync_status;

        let color = "initial";
        let text = "Pending";
        let message = row.sync_message || "";

        if (status === SALE_SYNC_STATUS.PENDING) {
          color = "orange";
          text = "Pending";
        } else if (status === SALE_SYNC_STATUS.FAILED) {
          color = "red";
          text = "Failed";
        }

        const isSynced = status === SALE_SYNC_STATUS.SYNCED_TO_CLOUD;

        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              {status === SALE_SYNC_STATUS.SYNCED_TO_CLOUD ? (
                <img src={TickIcon} alt="Synced" style={{ width: 16, height: 16 }} />
              ) : status === SALE_SYNC_STATUS.SYNCED_TO_LOCAL ? (
                <img src={LocalSync} alt="Local Sync" style={{ width: 16, height: 16 }} />
              ) : (
                <span style={{ color: color, fontWeight: 600 }}>{text}</span>
              )}
            </div>

            {!isSynced && message && (
              <small style={{ color: "gray", fontSize: "10px", lineHeight: "12px", maxWidth: "200px" }}>
                {message}
              </small>
            )}
          </div>
        );
      },
    },
    {
      id: "status",
      label: "Status",
      type: "component",
      viewRender: (row) => {
        const status = row.status || "";
        let color = "#2b2b2b";
        if (status === "returned") color = "red";
        if (status === "buy-back") color = "orange";

        return <span style={{ color, fontWeight: 600, fontSize: "11px" }}>{StringHelper.formatLabel(status)}</span>;
      },
    },
    // { id: "discount", label: "Discount", type: "floatAmount" },
    // { id: "tax", label: "Tax", type: "floatAmount" },
    // { id: "due", label: "Due", type: "floatAmount" },
    { id: "total", label: "Total", type: "floatAmount" },
  ];

  const handleClose = (shouldRefresh) => {
    setSelectedSaleId(null);

    if (shouldRefresh === true) {
      handleSync();
    }
  };

  const handleSync = useCallback(async () => {
    setRefreshing(true);
    await SalesHelper.syncPendingSales();
    await loadShiftData();
    setTimeout(() => setRefreshing(false), 500);
  }, [loadShiftData]);

  const handleForceSync = useCallback(async () => {
    setRefreshing(true);
    await SalesHelper.forceSyncPendingSales();
    await loadShiftData();
    setTimeout(() => setRefreshing(false), 500);
  }, [loadShiftData]);

  useEffect(() => {
    if (shiftId) {
      loadShiftData();
    }
  }, [shiftId, loadShiftData]);

  useEffect(() => {
    const unsubscribeFn = eventsEmitter.on(EVENTS.EXPENSE_CREATED, (eventData) => {
      const currentShiftId = shiftId;
      if (!eventData?.shiftId || String(eventData.shiftId) === String(currentShiftId)) {
        loadShiftData(false);
      }
    });

    return () => {
      unsubscribeFn();
    };
  }, [shiftId, loadShiftData]);

  const handlePrintShiftReport = async () => {
    try {
      const reportText = await ReportHelper.getShiftReportPrintText({
        shiftOverview,
        paymentModeSummaries,
      });
      await ReportHelper.printReport({ reportText });
    } catch (err) {
      console.error("Print Shift Report failed:", err);
    }
  };

  return (
    <>
      <div>
        {/* Shift Overview */}
        <div className={styles.container}>
          <div className={styles.headerRow}>
            <div>
              <h2 className={styles.title}>Shift Overview</h2>
              <h3 className={styles.subTitle}>Key financials and status of the current shift</h3>
            </div>
            <div style={{ display: "flex", gap: "10px" }}>
              <button className={styles.refreshBtn} onClick={() => setShowEditedSales(true)} title="Edited Sales">
                <h5>Edited sales</h5>
              </button>
              <button className={styles.refreshBtn} onClick={() => setShowDeletedCarts(true)} title="Deleted Carts">
                <h5>Deleted Carts</h5>
              </button>
              <button
                className={`${styles.refreshBtn} ${refreshing ? styles.spinning : ""}`}
                onClick={handleForceSync}
                disabled={loading || refreshing}
                title="Force Sync Data"
              >
                <img src={SyncIcon} alt="sync" />
                <h5>Force Sync Data</h5>
              </button>
              <button
                className={`${styles.refreshBtn} ${refreshing ? styles.spinning : ""}`}
                onClick={handleSync}
                disabled={loading || refreshing}
                title="Sync Data"
              >
                <img src={SyncIcon} alt="sync" />
                <h5>Sync Data</h5>
              </button>
            </div>
          </div>
          {loading ? (
            <SkeletonLoading isOpen={true} count={12} height={60} lg={3} />
          ) : (
            <TableContainer component={Paper} className={styles.overViewTable}>
              <Table size="small">
                <TableBody>
                  <TableRow className={styles.tableRow}>
                    <TableCell className={styles.tableCell} style={{ borderTopLeftRadius: "15px" }}>
                      <span className={styles.tableCellHead}>Name</span>
                      <span className={styles.tableCellValue}>{shiftOverview.name}</span>
                    </TableCell>
                    <TableCell className={styles.tableCell}>
                      <span className={styles.tableCellHead}>No. of Invoices</span>
                      <span className={styles.tableCellValue}>{shiftOverview.noOfInvoices}</span>
                    </TableCell>
                    <TableCell className={styles.tableCell}>
                      <span className={styles.tableCellHead}>Loyalty Sale</span>
                      <span className={styles.tableCellValue}>{roundOff(shiftOverview.totalLoyaltySales || 0)}</span>
                    </TableCell>
                    <TableCell className={styles.tableCell} style={{ borderTopRightRadius: "15px" }}>
                      <span className={styles.tableCellHead}>Purchase Amount</span>
                      <span className={styles.tableCellValue}>{roundOff(shiftOverview.totalPurchaseAmount || 0)}</span>
                    </TableCell>
                  </TableRow>

                  <TableRow className={styles.tableRow}>
                    <TableCell className={styles.tableCell}>
                      <span className={styles.tableCellHead}>Shift ID</span>
                      <span className={styles.tableCellValue}>{shiftOverview.shiftId}</span>
                    </TableCell>
                    <TableCell className={styles.tableCell}>
                      <span className={styles.tableCellHead}>Opening Balance</span>
                      <span className={styles.tableCellValue}>{roundOff(shiftOverview.openingBalance)}</span>
                    </TableCell>
                    <TableCell className={styles.tableCell}>
                      <span className={styles.tableCellHead}>Credit Sales</span>
                      <span className={styles.tableCellValue}>{roundOff(shiftOverview.totalCredits)}</span>
                    </TableCell>
                    <TableCell className={styles.tableCell}>
                      <span className={styles.tableCellHead}>Stock Damage</span>
                      <span className={styles.tableCellValue}>{roundOff(shiftOverview.totalStockDamage || 0)}</span>
                    </TableCell>
                  </TableRow>

                  <TableRow className={styles.tableRow}>
                    <TableCell className={styles.tableCell}>
                      <span className={styles.tableCellHead}>Opened At</span>
                      <span className={styles.tableCellValue}>
                        {DateTimeHelpers.formatDateTime(shiftOverview.opened)}
                      </span>
                    </TableCell>
                    <TableCell className={styles.tableCell}>
                      <span className={styles.tableCellHead}>Total Bill Amount</span>
                      <span className={styles.tableCellValue}>{roundOff(shiftOverview.totalBillAmount)}</span>
                    </TableCell>

                    <TableCell className={styles.tableCell}>
                      <span className={styles.tableCellHead}>Vendor Payments</span>
                      <span className={styles.tableCellValue}>{roundOff(shiftOverview.totalVendorPayments || 0)}</span>
                    </TableCell>
                    <TableCell className={styles.tableCell}>
                      <span className={styles.tableCellHead}>Total charges</span>
                      <span className={styles.tableCellValue}>{roundOff(shiftOverview.totalCharges)}</span>
                    </TableCell>
                  </TableRow>
                  <TableRow className={styles.tableRow}>
                    <TableCell className={styles.tableCell}>
                      <span className={styles.tableCellHead}>Cashier Name</span>
                      <span className={styles.tableCellValue}>{shiftOverview.cashierName || "-"}</span>
                    </TableCell>
                    <TableCell className={styles.tableCell}>
                      <span className={styles.tableCellHead}>Total Discount</span>
                      <span className={styles.tableCellValue}>{roundOff(shiftOverview.totalDiscount)}</span>
                    </TableCell>
                    <TableCell className={styles.tableCell}>
                      <span className={styles.tableCellHead}>Total Expenses</span>
                      <span className={styles.tableCellValue}>{roundOff(shiftOverview.totalExpenses)}</span>
                    </TableCell>
                    <TableCell className={styles.tableCell}>
                      <span className={styles.tableCellHead}>Closing Balance</span>
                      <span className={styles.tableCellValue}>{roundOff(shiftOverview.closingBalance)}</span>
                    </TableCell>
                  </TableRow>
                  <TableRow className={styles.tableRow}>
                    <TableCell className={styles.tableCell} style={{ borderBottomLeftRadius: "15px" }}>
                      <span className={styles.tableCellHead}>Status</span>
                      <span
                        className={
                          shiftOverview.status === SHIFT_STATUS.CLOSED ? styles.statusClosed : styles.statusOpen
                        }
                      >
                        {shiftOverview.status}
                      </span>
                    </TableCell>
                    <TableCell className={styles.tableCell}>
                      <span className={styles.tableCellHead}>Total Round Off</span>
                      <span className={styles.tableCellValue}>{roundOff(shiftOverview.totalRoundOffAmount)}</span>
                    </TableCell>
                    <TableCell className={styles.tableCell}>
                      <span className={styles.tableCellHead}>Credit Received</span>
                      <span className={styles.tableCellValue}>{roundOff(shiftOverview.totalCreditReceived || 0)}</span>
                    </TableCell>
                    <TableCell className={styles.tableCell} style={{ borderBottomRightRadius: "15px" }}></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <div className={styles.divider}></div>

          <div>
            {loading ? (
              <SkeletonLoading isOpen={true} count={8} height={45} xs={12} />
            ) : (
              <DataTable rows={paymentModeSummaries} columns={paymentColumns} />
            )}
          </div>
        </div>

        <div className={styles.shiftOrdersSection}>
          <div className={styles.headerSection}>
            <div>
              <h2 className={styles.title}>Shift Orders</h2>
              <h3 className={styles.subTitle}>List of invoices and sales activity</h3>
            </div>
            <ButtonRed
              style={{ marginRight: "10px" }}
              onClick={handlePrintShiftReport}
              startIcon={<img src={PrinterImg} alt="Print" className={styles.iconSmall} />}
            >
              Shift Report
            </ButtonRed>
          </div>
          {loading ? (
            <SkeletonLoading isOpen={true} count={8} height={45} xs={12} />
          ) : shiftOrders.length > 0 ? (
            <DataTable
              rows={shiftOrders}
              columns={shiftOrdersColumns}
              rowClickListener={(row) => setSelectedSaleId(row.local_id)}
            />
          ) : (
            <EmptyState title="Shift Orders Summary" />
          )}
        </div>

        {selectedSaleId && <SaleDetailsDialog open={!!selectedSaleId} onClose={handleClose} saleId={selectedSaleId} />}

        {selectedCashBook && (
          <ShiftPaymentCashBookPopup rowData={selectedCashBook} toggleViewCashBook={() => setSelectedCashBook(null)} />
        )}

        {showDeletedCarts && (
          <DeletedCartsPopup onClose={() => setShowDeletedCarts(false)} deletedCarts={deletedCarts} shiftId={shiftId} />
        )}

        {showEditedSales && (
          <EditedSalesPopup onClose={() => setShowEditedSales(false)} editedSales={editedSales} shiftId={shiftId} />
        )}
      </div>
    </>
  );
};

export default ShiftReport;
